import { alpha, PaletteMode, Shadows } from '@mui/material';
import type { TokenOverrides } from '../index';
import type { ColorTokens, ScaleTokens } from '../semantic';
import {
  derivedColors,
  exoColors,
  exoFonts,
  exoMotion,
  exoOpacity,
  exoPalette,
  exoShadowOffsets,
} from './exosphere';

// Boomi's count-based chart sequence: one series is coral, two are coral and
// navy, and so on up to ten.
const getChartSequences = (mode: PaletteMode) => {
  const exo = exoColors[mode];
  const p = exoPalette;
  const coral = exo['data-solid-coral'];
  const navy = exo['data-solid-navy'];
  const green = exo['data-solid-green'];
  const periwinkle = exo['data-solid-periwinkle'];
  // Exosphere's navy is navy-50 in dark mode and its periwinkle is
  // periwinkle-40 in light mode, so these steps move to stay distinct.
  const midNavy = mode === 'dark' ? p['navy-30'] : p['navy-50'];
  const fourthOfFive = mode === 'light' ? p['periwinkle-70'] : periwinkle;
  const tail = [
    midNavy,
    p['navy-40'],
    green,
    p['periwinkle-70'],
    p['periwinkle-40'],
  ];
  return [
    [coral],
    [coral, navy],
    [coral, navy, p['navy-40']],
    [coral, navy, green, periwinkle],
    [coral, navy, green, fourthOfFive, p['periwinkle-40']],
    [coral, navy, midNavy, green, p['periwinkle-70'], p['periwinkle-40']],
    [coral, navy, ...tail],
    [coral, navy, ...tail, p['periwinkle-30']],
    [exo['data-solid-purple'], coral, navy, ...tail, p['periwinkle-30']],
    [
      exo['data-solid-purple'],
      coral,
      navy,
      ...tail,
      p['periwinkle-30'],
      exo['data-solid-blue'],
    ],
  ].map((sequence) =>
    sequence.map((primary) => ({
      primary,
      light: alpha(primary, exoOpacity['2']),
    })),
  );
};

export const getBoomiColorOverrides = (
  mode: PaletteMode,
): TokenOverrides<ColorTokens> => {
  const exo = exoColors[mode];
  const derived = derivedColors[mode];
  const halo = (color: string) => alpha(color, exoOpacity['2']);
  const neutral = exo['data-solid-background-gray'];
  const onDark = (opacity: number) => alpha(exoPalette.white, opacity);

  return {
    content: {
      onAccent: {
        primary: exo['font-inverse'],
        secondary: exo['font-inverse'],
      },
      translucent: {
        primary: exo.font,
        secondary: exo['font-secondary'],
        disabled: exo['font-disabled'],
      },
    },
    surface: {
      translucent: {
        selected: derived['highlight/selected'],
        ...(mode === 'dark' && {
          hover: onDark(0.08),
          focus: onDark(0.12),
          disabled: onDark(0.12),
        }),
      },
      backdrop: derived.scrim,
    },
    border: {
      translucent: exo.border,
    },
    icon: {
      primary: exo.icon,
      inverse: exo['icon-inverse'],
      copied: exo['font-inverse'],
      ...(mode === 'dark' && {
        translucent: { active: exoPalette.white, disabled: onDark(0.3) },
      }),
    },
    accent: {
      primaryStrong: exo['background-selected-hover'],
      secondaryStrong: exo['background-selected-hover'],
    },
    status: {
      error: {
        fill: halo(derived['derived/data-danger']),
        fillSoft: halo(derived['derived/data-danger']),
      },
      warning: {
        fill: halo(derived['derived/data-warning']),
        fillStrong: halo(derived['derived/data-warning']),
      },
      success: {
        fill: halo(exo['data-solid-green']),
        fillStrong: halo(exo['data-solid-green']),
      },
      neutral: { fill: halo(neutral) },
    },
    chart: {
      status: {
        success: exo['data-solid-green'],
        successStrong: exo['data-solid-green'],
        warning: derived['derived/data-warning'],
        danger: derived['derived/data-danger'],
      },
      score: {
        low: derived['derived/data-danger'],
        lowStrong: derived['derived/data-danger'],
        medium: derived['derived/data-warning'],
        high: exo['data-solid-green'],
      },
      neutral,
      band: halo(neutral),
      seriesSequences: getChartSequences(mode),
    },
    code: {
      editorIcon: exo.icon,
    },
    identity: {
      // Exosphere avatar colors, the same in both modes.
      avatars: [
        { background: exoPalette['coral-50'], text: exoPalette['navy-80'] },
        { background: exoPalette['navy-70'], text: exoPalette.white },
        { background: exoPalette['purple-50'], text: exoPalette.white },
        { background: exoPalette['periwinkle-50'], text: exoPalette.white },
        { background: exoPalette['blue-60'], text: exoPalette.white },
        { background: exoPalette['navy-10'], text: exoPalette['navy-80'] },
      ],
    },
  };
};

export const getBoomiScaleOverrides = (
  mode: PaletteMode,
): TokenOverrides<ScaleTokens> => {
  const steps = getBoomiShadowSteps(mode);
  return {
    fontFamily: {
      sans: exoFonts.family,
      sansFace: "'Noto Sans'",
      mono: exoFonts.monospace,
      brand: exoFonts.brand,
    },
    fontWeight: {
      medium: exoFonts.weightSemiBold,
    },
    motion: {
      duration: {
        shortest: exoMotion['time-fast'],
        shorter: exoMotion['time-standard'],
        short: exoMotion['time-standard'],
        standard: exoMotion['time-standard'],
        complex: exoMotion['time-moderate'],
        enteringScreen: exoMotion['time-medium'],
        leavingScreen: exoMotion['time-standard'],
      },
      easing: {
        easeInOut: exoMotion['curve-standard'],
        easeOut: exoMotion['curve-decelerate'],
        easeIn: exoMotion['curve-accelerate'],
        sharp: exoMotion['curve-standard'],
      },
    },
    // One Exosphere step per role: 200 for cards and raised buttons, 300 for
    // tooltips, 400 for popovers and pickers, 500 for full-page panels and 100
    // for small markers.
    shadow: {
      sm: steps['200'],
      smSoft: steps['200'],
      smMedium: steps['200'],
      mdTight: steps['200'],
      md: steps['400'],
      lg: steps['400'],
      float: steps['500'],
      glow: steps['100'],
      halo: steps['300'],
      haze: steps['300'],
    },
  };
};

export const getBoomiShadowSteps = (mode: PaletteMode) => {
  const color = exoColors[mode]['shadow-weak'];
  return {
    '100': `${exoShadowOffsets['100']} ${color}`,
    '200': `${exoShadowOffsets['200']} ${color}`,
    '300': `${exoShadowOffsets['300']} ${color}`,
    '400': `${exoShadowOffsets['400']} ${color}`,
    '500': `${exoShadowOffsets['500']} ${color}`,
  };
};

// MUI elevations map to the Exosphere shadow with the closest blur.
export const getBoomiElevations = (mode: PaletteMode): Shadows => {
  const steps = getBoomiShadowSteps(mode);
  const {
    '100': s100,
    '200': s200,
    '300': s300,
    '400': s400,
    '500': s500,
  } = steps;
  return [
    'none',
    s100,
    s200,
    s200,
    s300,
    s300,
    s300,
    s300,
    s400,
    s400,
    s400,
    s400,
    s400,
    s400,
    s400,
    s400,
    s500,
    s500,
    s500,
    s500,
    s500,
    s500,
    s500,
    s500,
    s500,
  ];
};
